import { useState, useEffect } from 'react';
import Input, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import flags from 'react-phone-number-input/flags';
import { FormItem, FormLabel, FormMessage } from './form';

const PhoneInput = ({
  form,
  name,
  placeholder = 'Enter phone number',
  label = 'Phone Number',
  required = false,
  isFax = false,
  labelClassName,
  inputClassName,
}: {
  form: any;
  name: string;
  placeholder?: string;
  label?: string;
  required?: boolean;
  isFax?: boolean;
  labelClassName?: string;
  inputClassName?: string;
}) => {
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);

  // Get the error for this field - handle nested array fields
  const getFieldError = () => {
    const parts = name.split('.');
    let error = form.formState.errors;
    
    for (const part of parts) {
      if (!error) break;
      // Handle array indices
      if (/^\d+$/.test(part)) {
        error = error[parseInt(part)];
      } else {
        error = error[part];
      }
    }
    
    return error?.message || '';
  };

  // Simple initial value conversion
  useEffect(() => {
    const initialValue = form.getValues(name);
    if (initialValue && typeof initialValue === 'string') {
      // Clean the number and add +1 if it looks like a US number without country code
      const cleaned = initialValue.replace(/\D/g, '');
      if (cleaned.length === 10) {
        form.setValue(name, `+1${cleaned}`);
      }
    }
  }, []); // Empty dependency array - only run once

  const handleChange = (val = '') => {
    form.setValue(name, val);
    // If field is optional and empty, clear errors
    if (!val || val.trim() === '') {
      if (!required) {
        form.clearErrors(name);
        setError('');
        // Trigger form validation to update formState.isValid
        form.trigger(name);
        return;
      }
    }
    // Validate only if there's a value or if field is required
    if (val && !isValidPhoneNumber(val, 'US')) {
      form.setError(name, {
        message: `Please enter a valid US ${isFax ? 'Fax' : 'Phone'} number.`,
      });
    } else {
      form.clearErrors(name);
      // Trigger form validation to update formState.isValid
      form.trigger(name);
    }
    setError(''); // reset error as user types
  };

  const handleBlur = () => {
    setTouched(true);
    const currentValue = form.getValues(name);

    if (!currentValue || currentValue.trim() === '') {
      if (required) {
        setError(`${isFax ? 'Fax' : 'Phone'} number is required.`);
        form.setError(name, {
          message: `${isFax ? 'Fax' : 'Phone'} number is required.`,
        });
      } else {
        // Optional field - clear any errors if empty
        setError('');
        form.clearErrors(name);
        form.trigger(name);
      }
    } else if (!isValidPhoneNumber(currentValue, 'US')) {
      // Only show validation error if field has a value (required or optional)
      setError(`Please enter a valid US ${isFax ? 'Fax' : 'Phone'} number.`);
      form.setError(name, {
        message: `Please enter a valid US ${isFax ? 'Fax' : 'Phone'} number.`,
      });
    } else {
      setError('');
      form.clearErrors(name);
      // Trigger form validation to update formState.isValid
      form.trigger(name);
    }
  };

  const formError = getFieldError();
  const displayError = error || formError;

  return (
    <FormItem>
      {label && <FormLabel className={[required ? 'required' : '', labelClassName].filter(Boolean).join(' ')}>{label}</FormLabel>}
      <Input
        international
        defaultCountry="US"
        countryCallingCodeEditable={false}
        value={form.watch(name) || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        flags={flags}
        placeholder={placeholder}
        countrySelectProps={{ disabled: true }}
        className={inputClassName}
      />
      {displayError && (
        <p className="text-[0.8rem] text-red-500 mt-1">{displayError}</p>
      )}
    </FormItem>
  );
};

export default PhoneInput;
